// Admin → Communications: trigger product broadcasts safely (preview → test →
// dry-run → send) and keep a persistent record of everything mailed.
import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Eye, FlaskConical, Send, RefreshCw } from "lucide-react";

const TYPE_COLORS = { broadcast: "#10B981", test: "#3B82F6", report: "#A78BFA", reconcile: "#F59E0B" };

export const CommunicationsCard = () => {
  const [history, setHistory] = useState([]);
  const [dryRun, setDryRun] = useState(null);
  const [busy, setBusy] = useState("");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [status, setStatus] = useState("");

  const loadHistory = useCallback(() => {
    api.get("/admin/communications").then(({ data }) => setHistory(data.communications || [])).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const run = async (label, fn) => {
    setBusy(label); setStatus("");
    try { await fn(); } catch (e) { setStatus(e?.response?.data?.detail || e.message); }
    finally { setBusy(""); loadHistory(); }
  };

  const preview = () => run("preview", async () => {
    const { data } = await api.get("/admin/email-preview/assistant", { responseType: "text" });
    setPreviewHtml(data);
  });
  const sendTest = () => run("test", async () => {
    const { data } = await api.post("/admin/email-preview/assistant/send-test");
    setStatus(data.message);
  });
  const doDryRun = () => run("dry", async () => {
    const { data } = await api.post("/admin/announcements/assistant/send?dry_run=true");
    setDryRun(data);
  });
  const markSent = () => run("mark", async () => {
    if (!window.confirm(`Mark ${dryRun.recipients.length} user(s) as already sent WITHOUT emailing them? Use only if a previous broadcast actually delivered but failed to record.`)) return;
    const { data } = await api.post("/admin/announcements/assistant/mark-sent");
    setStatus(`Marked ${data.count} user(s) as already sent — no emails were sent.`);
    setDryRun(null);
  });
  const doSend = () => run("send", async () => {
    if (!window.confirm(`Send the Portfolio Assistant announcement to ${dryRun.recipients.length} user(s)? This cannot be undone.`)) return;
    const { data } = await api.post("/admin/announcements/assistant/send?dry_run=false");
    setStatus(`Sent to ${data.sent.length} · failed ${data.failed.length} · report emailed to ${data.report_emailed_to}`);
    setDryRun(null);
  });

  const btn = "inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1.5 border rounded-sm hover:bg-[var(--hoip-hover)] transition-colors disabled:opacity-40";
  return (
    <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="communications-card">
      <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: "var(--hoip-border)" }}>
        <Mail className="w-4 h-4 text-emerald-400" />
        <span className="text-xs font-medium" style={{ color: "var(--hoip-text-secondary)" }}>
          Communications — broadcasts & mail log
        </span>
      </div>
      <div className="p-4 border-b space-y-2" style={{ borderColor: "var(--hoip-divider)" }}>
        <div className="text-[11px] font-bold" style={{ color: "var(--hoip-text-primary)" }}>
          Portfolio Assistant announcement
        </div>
        <div className="flex flex-wrap gap-2">
          <button className={btn} style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
            disabled={!!busy} onClick={preview} data-testid="comm-preview-btn">
            <Eye className="w-3 h-3" /> Preview
          </button>
          <button className={btn} style={{ borderColor: "var(--hoip-border)", color: "#3B82F6" }}
            disabled={!!busy} onClick={sendTest} data-testid="comm-test-btn">
            <FlaskConical className="w-3 h-3" /> {busy === "test" ? "Sending…" : "Send test to me"}
          </button>
          <button className={btn} style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
            disabled={!!busy} onClick={doDryRun} data-testid="comm-dryrun-btn">
            <RefreshCw className="w-3 h-3" /> {busy === "dry" ? "Checking…" : "Dry run (who gets it?)"}
          </button>
          {dryRun && dryRun.recipients.length > 0 && (
            <button className={btn} style={{ borderColor: "#10B98155", color: "#10B981" }}
              disabled={!!busy} onClick={doSend} data-testid="comm-send-btn">
              <Send className="w-3 h-3" /> {busy === "send" ? "Sending…" : `Send to ${dryRun.recipients.length} users`}
            </button>
          )}
          {dryRun && dryRun.recipients.length > 0 && (
            <button className={btn} style={{ borderColor: "var(--hoip-border)", color: "#F59E0B" }}
              disabled={!!busy} onClick={markSent} data-testid="comm-mark-sent-btn"
              title="Flag everyone in the dry-run list as already mailed, without sending anything.">
              {busy === "mark" ? "Marking…" : "Mark as already sent (no email)"}
            </button>
          )}
        </div>
        {dryRun && (
          <div className="text-[10px] leading-relaxed" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="comm-dryrun-result">
            {dryRun.recipients.length === 0
              ? "No pending recipients — everyone eligible has already been mailed."
              : <>Will send to <span style={{ color: "var(--hoip-text-primary)" }}>{dryRun.recipients.length}</span>: {dryRun.recipients.join(", ")}</>}
            {" "}· {dryRun.skipped_test_accounts_or_already_sent} skipped (test accounts or already sent). A delivery report is emailed to you after sending.
          </div>
        )}
        {status && <div className="text-[10px]" style={{ color: "#3B82F6" }} data-testid="comm-status">{status}</div>}
      </div>
      <div className="p-4">
        <div className="text-[10px] font-bold mb-2" style={{ color: "var(--hoip-text-tertiary)" }}>MAIL LOG ({history.length})</div>
        {history.length === 0 ? (
          <div className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>No communications recorded yet.</div>
        ) : (
          <table className="w-full dense-table" data-testid="comm-history-table">
            <thead>
              <tr className="text-left text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                <th className="py-1">When (UTC)</th><th>Type</th><th>Subject</th><th className="text-right">Recipients</th><th>By</th>
              </tr>
            </thead>
            <tbody>
              {history.map((c) => (
                <tr key={c.id} className="border-t text-[10px]" style={{ borderColor: "var(--hoip-divider)" }}>
                  <td className="py-1.5 font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>{(c.ts || "").slice(0, 16).replace("T", " ")}</td>
                  <td><span className="text-[8px] font-bold px-1.5 py-0.5 rounded-sm border"
                    style={{ color: TYPE_COLORS[c.type] || "var(--hoip-text-secondary)", borderColor: (TYPE_COLORS[c.type] || "#666") + "44" }}>
                    {(c.type || "").toUpperCase()}</span></td>
                  <td style={{ color: "var(--hoip-text-secondary)" }}>{c.subject}</td>
                  <td className="text-right font-mono-data cursor-help" style={{ color: "var(--hoip-text-primary)" }}
                    title={(c.recipients || []).join("\n")}>
                    {c.recipient_count}{(c.failed || []).length > 0 ? <span className="text-red-400"> ({c.failed.length} failed)</span> : null}
                  </td>
                  <td style={{ color: "var(--hoip-text-tertiary)" }}>{c.triggered_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Dialog open={!!previewHtml} onOpenChange={(o) => !o && setPreviewHtml(null)}>
        <DialogContent className="max-w-2xl bg-[#0A0A0A] border-[#27272A]">
          <DialogHeader><DialogTitle className="text-xs text-gray-300">Email preview — Assistant announcement</DialogTitle></DialogHeader>
          <iframe title="email-preview" srcDoc={previewHtml || ""} className="w-full rounded-sm" style={{ height: "65vh", background: "#000", border: "1px solid #27272A" }} />
        </DialogContent>
      </Dialog>
    </div>
  );
};
